import React from 'react'

/**
 * Reusable editor for defining output variable mappings.
 *
 * Stores an array of { name, from, description } on the simulation's pluginConfig.
 * This serves as documentation for what values the simulation produces and can be
 * referenced when building chain outputMap/inputMap entries.
 *
 * Props:
 *   outputMap      — current array of output entries
 *   onChange        — callback(newOutputMap)
 *   sourceHint     — the prefix for `from` paths (e.g., "cellValues", "selections")
 *   sourcePlaceholder — example path (e.g., "cellValues.B2", "selections.interest_rate")
 */

const fieldStyle = {
  padding: '4px 8px',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  fontSize: '13px',
  boxSizing: 'border-box',
}

export const OutputMapEditor = ({ outputMap, onChange, sourceHint, sourcePlaceholder }) => {
  const entries = outputMap || []

  const updateEntry = (index, field, value) => {
    const updated = entries.map((e, i) => (i === index ? { ...e, [field]: value } : e))
    onChange(updated)
  }

  const removeEntry = (index) => {
    onChange(entries.filter((_, i) => i !== index))
  }

  const addEntry = () => {
    onChange([...entries, { name: '', from: sourceHint ? `${sourceHint}.` : '', description: '' }])
  }

  return (
    <div>
      <label style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px', display: 'block' }}>
        Output Values (for chain mapping)
      </label>
      <p style={{ fontSize: '12px', color: '#6c757d', margin: '0 0 8px 0' }}>
        Define named output variables this simulation produces. These map to <code>outputMap</code> entries
        when this simulation is used in a chain.
      </p>

      {entries.length > 0 ? (
        <div>
          {/* Header row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '140px 200px 1fr 30px',
            gap: '4px',
            marginBottom: '4px',
            fontSize: '11px',
            fontWeight: 600,
            color: '#6c757d',
          }}>
            <span>Variable Name</span>
            <span>Source Path</span>
            <span>Description</span>
            <span />
          </div>
          {entries.map((entry, index) => (
            <div key={index} style={{
              display: 'grid',
              gridTemplateColumns: '140px 200px 1fr 30px',
              gap: '4px',
              marginBottom: '4px',
              alignItems: 'center',
            }}>
              <input
                type="text"
                value={entry.name || ''}
                onChange={(e) => updateEntry(index, 'name', e.target.value)}
                style={fieldStyle}
                placeholder="variable_name"
              />
              <input
                type="text"
                value={entry.from || ''}
                onChange={(e) => updateEntry(index, 'from', e.target.value)}
                style={{ ...fieldStyle, fontFamily: 'monospace', fontSize: '12px' }}
                placeholder={sourcePlaceholder || 'data.path'}
              />
              <input
                type="text"
                value={entry.description || ''}
                onChange={(e) => updateEntry(index, 'description', e.target.value)}
                style={fieldStyle}
                placeholder="Optional description"
              />
              <button
                type="button"
                onClick={() => removeEntry(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#dc3545',
                  cursor: 'pointer',
                  fontSize: '14px',
                  padding: '0',
                  textAlign: 'center',
                }}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: '12px', color: '#adb5bd', marginBottom: '4px' }}>
          No output values defined. Add entries if this simulation will be used in a chain.
        </div>
      )}

      <button
        type="button"
        onClick={addEntry}
        className="btn btn-myed btn-default btn-default-myed btn-sm"
        style={{ marginTop: '4px' }}
      >
        + Add Output
      </button>
    </div>
  )
}
